import { Button, Modal } from '@dev-spendesk/grapes';
import React, { type Dispatch, type SetStateAction } from 'react';

import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { useNotifications } from 'modules/app/notifications';
import { type AccountingSoftware } from 'modules/bookkeep/integration/status';
import { type IntegrationReverseChargeAccount } from 'modules/bookkeep/settings/accounting';
import { useSetTaxAccountMutation } from 'modules/bookkeep/settings/integrations/hooks/useSetTaxAccountMutation';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { routeFor, routes } from 'src/core/constants/routes';

import { renderReverseChargeAddEditForm } from './renderAddEditForm';
import { CodeInUseCallout } from '../../../../components/CodeInUseCallout/CodeInUseCallout';
import { getReverseChargeAddEditDescription } from '../../translations';
import { type ModalState } from '../ModalState';
import styles from '../TaxAccountLocalOnlySection.module.css';

interface Props {
  formState: ModalState;
  setFormState: Dispatch<SetStateAction<ModalState>>;
  integration: AccountingSoftware;
  reverseChargeAccounts: IntegrationReverseChargeAccount[];
  hideErrorLink?: boolean;
}

export const ReverseChargeAddModal = ({
  formState,
  setFormState,
  integration,
  reverseChargeAccounts,
  hideErrorLink = false,
}: Props) => {
  const { t } = useTranslation('global');
  const { dangerNotif } = useNotifications();
  const companyId = useCompanyId();

  const [setTaxAccount, setTaxAccountQueryState, clearErrors] =
    useSetTaxAccountMutation();

  const isAddModalWithCode =
    formState.kind === 'addReverseCharge' && !!formState.codeCredit;

  const duplicatedError =
    isAddModalWithCode &&
    setTaxAccountQueryState.status === 'success' &&
    setTaxAccountQueryState.data.outcome === 'notSet' &&
    (setTaxAccountQueryState.data.reason === 'nameAndRateAlreadyExists' ||
      setTaxAccountQueryState.data.reason === 'codeAlreadyExists');

  const duplicatedCurrentAccount =
    duplicatedError &&
    formState.kind === 'addReverseCharge' &&
    !formState.inputChanged &&
    reverseChargeAccounts.find(
      (account) =>
        account.codeDebit === formState.codeDebit ||
        account.codeCredit === formState.codeCredit,
    );
  return (
    <Modal
      title={t(
        'bookkeep.integrations.settings.reverseChargeAccountsTable.addReverseChargeAccount',
      )}
      isOpen={formState.kind === 'addReverseCharge'}
      iconName="plus"
      iconVariant="info"
      actions={[
        <Button
          key="cancel"
          onClick={() => {
            setFormState({ kind: 'closed' });
            clearErrors();
          }}
          text={t('misc.cancel')}
          variant="secondaryNeutral"
        />,
        <Button
          key="confirmSection"
          onClick={async () => {
            if (formState.kind === 'addReverseCharge') {
              setFormState({ ...formState, inputChanged: false });

              try {
                const result = await setTaxAccount({
                  name: formState.name,
                  codeDebit: formState.codeDebit,
                  codeCredit: formState.codeCredit,
                  type: 'reverseCharge',
                  isArchived: false,
                });

                if (result.outcome === 'set') {
                  setFormState({ kind: 'closed' });
                }
              } catch {
                dangerNotif(t('misc.errors.unknownError'));
              }
            }
          }}
          text={t(
            'bookkeep.integrations.settings.reverseChargeAccountsTable.confirmSelection',
          )}
          variant="primaryBrand"
          isDisabled={
            formState.kind === 'addReverseCharge' &&
            (!formState.name ||
              !formState.codeCredit ||
              !formState.codeDebit ||
              (duplicatedError && !formState.inputChanged))
          }
        />,
      ]}
    >
      <>
        <p className={styles.subtitle}>
          {getReverseChargeAddEditDescription(t, integration)}
        </p>

        {renderReverseChargeAddEditForm(integration, formState, setFormState)}

        {duplicatedCurrentAccount && (
          <CodeInUseCallout
            accountInfo={`${duplicatedCurrentAccount.name} - ${
              formState.kind === 'addReverseCharge' &&
              duplicatedCurrentAccount.codeDebit === formState.codeDebit
                ? duplicatedCurrentAccount.codeDebit
                : duplicatedCurrentAccount.codeCredit
            }`}
            linkTo={
              !hideErrorLink
                ? routeFor(routes.COMPANY_ACCOUNTING_TAX_ACCOUNTS.path, {
                    company: companyId,
                  })
                : undefined
            }
          />
        )}
      </>
    </Modal>
  );
};
